import React, { createContext, useState, useCallback, useContext } from 'react'
import { defaultTemplates, Template } from './templates'
import { EditorContent } from '../editorContent/types'

// Cross-domain communication interfaces
export interface TemplateSaveOptions {
	includeCurrentContent?: boolean // Use current editor content
	templateName?: string // Override template name
	templateDescription?: string // Override description
}

export interface TemplateContextValue {
	// Template State
	templates: Template[]
	selectedTemplateId: string | null
	selectedTemplate: Template | null // Computed selected template

	// Template Actions
	selectTemplate: (templateId: string) => void // Clean selection
	applyTemplateToContent: (templateId: string) => void // Content integration
	saveTemplate: (options?: TemplateSaveOptions) => void // Enhanced save
	saveAsNewTemplate: (name?: string) => void // Create new template - name is optional

	// Cross-Domain Communication Events
	onTemplateApply?: (template: Template) => void // Notify Editor domain
	onContentRequest?: () => EditorContent // Request current content
	onTitleRequest?: () => string // Request current title
	onDirtyStateChange?: (isDirty: boolean) => void // Notify state changes
}

export interface TemplateProviderProps {
	children: React.ReactNode
	// Cross-domain event handlers (injected from parent)
	onTemplateApply?: (template: Template) => void
	onContentRequest?: () => EditorContent
	onTitleRequest?: () => string
	onDirtyStateChange?: (isDirty: boolean) => void
	// Initial state
	initialTemplateId?: string | null
}

const TemplateContext = createContext<TemplateContextValue | null>(null)

/**
 * TemplateProvider: Clean template state management with cross-domain communication
 *
 * This provider eliminates prop drilling by managing all template-related state
 * and provides event-based communication for cross-domain operations.
 *
 * Cross-domain operations (template → editor content) are handled through
 * injected callback functions rather than direct state coupling.
 */
export const TemplateProvider: React.FC<TemplateProviderProps> = ({
	children,
	onTemplateApply,
	onContentRequest,
	onTitleRequest,
	onDirtyStateChange,
	initialTemplateId = null,
}) => {
	// Template state - isolated to this domain
	const [templates] = useState<Template[]>(defaultTemplates)
	const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(initialTemplateId)

	// Computed values
	const selectedTemplate = templates.find((t) => t.id === selectedTemplateId) ?? null

	/**
	 * Clean template selection without direct content mutation
	 * The actual content change is handled by the parent through onTemplateApply
	 */
	const selectTemplate = useCallback(
		(templateId: string) => {
			const template = templates.find((t) => t.id === templateId)
			if (template) {
				setSelectedTemplateId(templateId)
				// Clean cross-domain notification - parent decides how to handle content change
				onTemplateApply?.(template)
			}
		},
		[templates, onTemplateApply],
	)

	/**
	 * Apply template to content - explicit action for UI clarity
	 * This is the same as selectTemplate but with clearer naming for different use cases
	 */
	const applyTemplateToContent = useCallback(
		(templateId: string) => {
			selectTemplate(templateId)
			// Notify that this action makes content dirty
			onDirtyStateChange?.(true)
		},
		[selectTemplate, onDirtyStateChange],
	)

	/**
	 * Enhanced save template functionality
	 * Uses cross-domain communication to access current editor content
	 */
	const saveTemplate = useCallback(
		(options: TemplateSaveOptions = {}) => {
			if (!selectedTemplateId) {
				// eslint-disable-next-line no-console
				console.warn('No template selected for saving')
				return
			}

			// Get current content through cross-domain communication
			const currentContent = options.includeCurrentContent ? onContentRequest?.() : null

			// eslint-disable-next-line no-console
			console.log('--- SIMULATING TEMPLATE SAVE (PUT) ---')
			// eslint-disable-next-line no-console
			console.log('Template save payload:', {
				id: selectedTemplateId,
				name: options.templateName,
				description: options.templateDescription,
				...(currentContent && {
					html: currentContent.html,
					css: currentContent.css,
				}),
			})

			// Notify that save action clears dirty state
			onDirtyStateChange?.(false)
		},
		[selectedTemplateId, onContentRequest, onDirtyStateChange],
	)

	/**
	 * Save as new template functionality
	 * Uses cross-domain communication to access current content and title
	 */
	const saveAsNewTemplate = useCallback(
		(customName?: string) => {
			// Get current data through cross-domain communication
			const currentContent = onContentRequest?.()
			const currentTitle = onTitleRequest?.()

			if (!currentContent) {
				// eslint-disable-next-line no-console
				console.warn('No content available for saving as new template')
				return
			}

			// Generate name from title if not provided
			const templateName = customName ?? (currentTitle ? `${currentTitle} (Copy)` : 'New Template')

			// eslint-disable-next-line no-console
			console.log('--- SIMULATING SAVE AS NEW TEMPLATE (POST) ---')
			// eslint-disable-next-line no-console
			console.log('New template payload:', {
				name: templateName,
				// eslint-disable-next-line
				description: `Custom template created from: ${currentTitle || 'Untitled'}`,
				html: currentContent.html,
				css: currentContent.css,
				variables: ['{{TITLE}}', '{{CONTENT}}'], // TODO: Extract from content automatically
			})

			// Note: In real implementation, this would update the templates state
			// with the newly created template and set it as selected
		},
		[onContentRequest, onTitleRequest],
	)

	const contextValue: TemplateContextValue = {
		// State
		templates,
		selectedTemplateId,
		selectedTemplate,

		// Actions
		selectTemplate,
		applyTemplateToContent,
		saveTemplate,
		saveAsNewTemplate,

		// Cross-domain communication (pass through for child hooks)
		onTemplateApply,
		onContentRequest,
		onTitleRequest,
		onDirtyStateChange,
	}

	return <TemplateContext.Provider value={contextValue}>{children}</TemplateContext.Provider>
}

/**
 * Base hook to access template context
 * Throws error if used outside TemplateProvider
 */
export const useTemplateContext = (): TemplateContextValue => {
	const context = useContext(TemplateContext)
	if (!context) {
		throw new Error('useTemplateContext must be used within a TemplateProvider')
	}
	return context
}
